import {
    formatValuationMonthYearParenthetical,
    pickLatestValuationDate,
    collectClientValuationUserIds,
} from "#app/domain/clients/clientActivesValuation";
import type {FinanceGroup} from "#app/domain/clients/financeGroup";
import {getActivesItemValuation, type ActivesValuationItem} from "#app/domain/funnel/activesValuation";

export type ClientValuationRecord = {
    clientId: number;
    sum: number;
    asOfDate: string | null;
    asOfLabel: string | null;
    updatedAt: number;
};

export type ClientValuationSource = {
    id: number;
    financeGroups?: FinanceGroup[] | null;
    finance_groups?: FinanceGroup[] | null;
    [key: string]: any;
};

export function buildClientValuationsIndex(
    clients: ClientValuationSource[],
    rows: ActivesValuationItem[],
    updatedAt: number = Date.now()
): Record<string, ClientValuationRecord> {
    const rowsByUserId = new Map<number, ActivesValuationItem[]>();
    for (const row of rows) {
        const uid = Number((row as ActivesValuationItem & {user_id?: any }).user_id);
        if (!Number.isFinite(uid) || uid <= 0) {
            continue;
        }
        const bucket = rowsByUserId.get(uid) ?? [];
        bucket.push(row);
        rowsByUserId.set(uid, bucket);
    }

    const byClientId: Record<string, ClientValuationRecord> = {};

    for (const client of clients) {
        const clientId = Number(client.id);
        if (!Number.isFinite(clientId) || clientId <= 0) {
            continue;
        }
        const financeGroups = client.financeGroups ?? client.finance_groups ?? [];
        const userIds = collectClientValuationUserIds(clientId, financeGroups, client as never);
        const matchedRows: ActivesValuationItem[] = [];
        for (const uid of userIds) {
            const bucket = rowsByUserId.get(uid);
            if (bucket) {
                matchedRows.push(...bucket);
            }
        }
        const sum = matchedRows.reduce((acc, row) => acc + getActivesItemValuation(row), 0);
        const asOfDate = pickLatestValuationDate(matchedRows);
        byClientId[String(clientId)] = {
            clientId,
            sum,
            asOfDate,
            asOfLabel: formatValuationMonthYearParenthetical(asOfDate),
            updatedAt,
        };
    }

    return byClientId;
}
